/**
 * Admin page for manual review (specification §8). Each check Morcast cannot decide alone, with
 * the post or video, its campaign and the evidence, and a decision with a note. For a video, the
 * reviewer sees Morcast's transcript and the upload's frames, each next to the public video at
 * the same time. A campaign's result is published only once every review of its items is decided.
 */

import { CircleCheck } from "lucide-react";
import { useState } from "react";

import {
  type GateRecord,
  type Review,
  type ReviewFrame,
  useDecideReview,
  useReviews,
} from "../../admin/reviews.js";
import { RequireAdmin } from "../../components/RequireAdmin.js";
import {
  Disclosure,
  EmptyState,
  ErrorMessage,
  Fact,
  Loading,
  PageHeader,
  styles,
} from "../../components/ui.js";
import { formatCount, formatUtc } from "../../format.js";

export function AdminReviews() {
  return (
    <RequireAdmin>
      <ReviewsPage />
    </RequireAdmin>
  );
}

function ReviewsPage() {
  const reviews = useReviews();
  return (
    <section className="max-w-5xl">
      <PageHeader eyebrow="Admin" title="Reviews" documentTitle="Reviews · Admin">
        Checks Morcast cannot decide alone, oldest first. Compare the evidence with the brief and
        decide. A failure takes the check's reason code, and every decision is recorded with its
        note.
      </PageHeader>
      <div className="mt-8">
        {reviews.isPending && <Loading />}
        {reviews.isError && <ErrorMessage error={reviews.error} />}
        {reviews.data?.length === 0 && (
          <EmptyState icon={CircleCheck} title="No check waits for review." />
        )}
        {reviews.data && reviews.data.length > 0 && (
          <ul className="space-y-4">
            {reviews.data.map((review) => (
              <ReviewCard key={`${review.submissionId}-${review.check}`} review={review} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/** What each check asks the reviewer to judge. */
const CHECKS: Record<Review["check"], { title: string; question: string }> = {
  edits: {
    title: "Edits",
    question: "Is the change one the brief allows? Otherwise the item fails as EDITED.",
  },
  rules: {
    title: "Rules",
    question: "Is this the creator's own post, not a repost? Otherwise it fails as DUPLICATE.",
  },
  elements: {
    title: "Required elements",
    question:
      "Does the content contain every required element? Otherwise it fails as MISSING_ELEMENT.",
  },
  media: {
    title: "Media match",
    question:
      "Is the upload the published video? Open the public video at each frame's time and compare. Otherwise it fails as MEDIA_MISMATCH.",
  },
  format: {
    title: "Format",
    question: "",
  },
  manipulation: {
    title: "Manipulation",
    question:
      "Is the quoted text aimed at the checker rather than the audience? If so, it fails as MANIPULATION. If not, clear it.",
  },
  semantic: {
    title: "Brief",
    question:
      "Does the content meet each requirement the runs disagree on, without anything the brief prohibits? Otherwise it fails as BRIEF_NOT_MET.",
  },
  visual: {
    title: "Visuals",
    question:
      "Do the images or frames meet each requirement the runs disagree on? Otherwise it fails as BRIEF_NOT_MET.",
  },
  fraud: {
    title: "Fraud",
    question:
      "Is this performance genuine, given the account's history and audience? Otherwise it fails as FRAUD.",
  },
};

/** What a format review asks: the model gate of the campaign's format. */
function formatQuestion(format: Review["campaign"]["format"]): string {
  return format === "INTEGRATION"
    ? "Does the declared segment contain the promotion of the brand? Otherwise it fails as PROMOTION_NOT_IN_SEGMENT."
    : "Is at least 4/5 of the video about the brand? Otherwise it fails as NOT_DEDICATED.";
}

function ReviewCard({ review }: { review: Review }) {
  const decide = useDecideReview();
  const [note, setNote] = useState("");
  const { title } = CHECKS[review.check];
  const question =
    review.check === "format"
      ? formatQuestion(review.campaign.format)
      : CHECKS[review.check].question;
  const { video } = review;
  const send = (outcome: "pass" | "fail") =>
    decide.mutate({
      submissionId: review.submissionId,
      check: review.check,
      outcome,
      note: note.trim(),
    });

  return (
    <li className={styles.card}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-semibold">
          {title}: {review.campaign.title}
        </h2>
        <span className="text-xs text-zinc-500 dark:text-zinc-400">
          Waiting since {formatUtc(review.since)}
        </span>
      </div>
      <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
        <Fact label="Brand">{review.campaign.brandName}</Fact>
        <Fact label={video ? "Video" : "Post"}>
          <a href={review.submission.url} className="break-all hover:underline">
            {review.submission.url}
          </a>
        </Fact>
        <Fact label="Submitted">{formatUtc(review.submission.receivedAt)}</Fact>
        {review.submission.segment && (
          <Fact label="Declared segment">
            {formatSeconds(review.submission.segment.start)} to{" "}
            {formatSeconds(review.submission.segment.end)}
          </Fact>
        )}
      </dl>

      <div className="mt-4 space-y-3 text-sm">
        {review.details.problem && <p>{review.details.problem}</p>}
        {review.check === "edits" && review.campaign.allowedEdits && (
          <p>
            <span className="font-medium">The brief allows:</span> {review.campaign.allowedEdits}
          </p>
        )}
        {review.details.reads?.map((read) => (
          <EditEvidence key={read.at} read={read} />
        ))}
        {review.check === "fraud" && (
          <FraudEvidence details={review.details} platform={review.campaign.platform} />
        )}
        {review.check === "manipulation" && <AuditEvidence details={review.details} />}
        {review.check === "media" && video && (
          <MediaEvidence review={review} frames={video.frames} />
        )}
        {review.check === "format" && <FormatEvidence review={review} />}
        {review.details.requirements?.map((entry) => (
          <RequirementEvidence key={entry.requirement} entry={entry} />
        ))}
        {review.details.prohibitions?.map((entry) => (
          <ProhibitionEvidence key={entry.prohibition} entry={entry} />
        ))}
        {review.check === "visual" && review.campaign.assets.length > 0 && (
          <BrandKit assets={review.campaign.assets} />
        )}
        {review.check === "visual" && video && <Frames frames={video.frames} />}
        {video?.transcript && <Transcript lines={video.transcript} by={video.transcriber} />}
        <p className="font-medium">{question}</p>
      </div>

      <label className="mt-3 block text-sm font-medium">
        Note
        <textarea
          rows={2}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          className={styles.input}
        />
      </label>
      <div className="mt-3 flex gap-3">
        <button
          type="button"
          disabled={decide.isPending || note.trim().length < 5}
          onClick={() => send("pass")}
          className={styles.primaryButton}
        >
          Pass
        </button>
        <button
          type="button"
          disabled={decide.isPending || note.trim().length < 5}
          onClick={() => send("fail")}
          className={styles.dangerButton}
        >
          Fail
        </button>
      </div>
      {decide.isError && <ErrorMessage error={decide.error} />}
    </li>
  );
}

/** What one read of an edited post or video showed, against the capture. */
function EditEvidence({ read }: { read: NonNullable<Review["details"]["reads"]>[number] }) {
  const when = read.at === "end" ? "At endAt" : "At Day 3";
  // A post's reads name the version people saw. A video has only one.
  const post = read.versionId !== undefined;
  if (!read.compared) {
    return post ? (
      <p>
        {when}, people saw version {read.versionId ?? "unknown"}, which X's answer did not include.
      </p>
    ) : (
      <p>{when}, YouTube's answer did not include the video.</p>
    );
  }
  if (!read.change) {
    return (
      <p>
        {when}, the {post ? "post" : "video"} was as captured.
      </p>
    );
  }
  const changes = [
    ["Text", read.change.text],
    ["Title", read.change.title],
    ["Description", read.change.description],
  ] as const;
  return (
    <div>
      <p>{post ? `${when}, people saw version ${read.versionId}:` : `${when}, the video read:`}</p>
      {changes.map(
        ([label, change]) =>
          change && (
            <dl key={label} className="mt-1 grid gap-2 sm:grid-cols-2">
              <Fact label={`${label} captured`}>
                <span className="whitespace-pre-line font-normal">{change.before}</span>
              </Fact>
              <Fact label={`${label} seen`}>
                <span className="whitespace-pre-line font-normal">{change.after}</span>
              </Fact>
            </dl>
          ),
      )}
      {read.change.durationSec && (
        <p>
          The duration changed from {formatSeconds(read.change.durationSec.before)} to{" "}
          {formatSeconds(read.change.durationSec.after)}.
        </p>
      )}
      {read.change.linksAdded.length > 0 && <p>Links added: {read.change.linksAdded.join(", ")}</p>}
      {read.change.linksRemoved.length > 0 && (
        <p>Links removed: {read.change.linksRemoved.join(", ")}</p>
      )}
    </div>
  );
}

/** Why the fraud standard sent an item to review: its metric against the account's history. */
function FraudEvidence({
  details,
  platform,
}: {
  details: Review["details"];
  platform: Review["campaign"]["platform"];
}) {
  const median =
    platform === "X"
      ? `${formatCount(details.median ?? "0")} impressions per post over the account's ${details.posts} posts in the 30 days before the start${details.truncated ? " (the first 1,000 read)" : ""}`
      : `${formatCount(details.median ?? "0")} views a day over the channel's 30 days before the start`;
  const basis =
    details.rule === "baseline"
      ? `10 times the median of ${median}`
      : `10 × M, ${formatCount(details.threshold ?? "0")}: ${
          details.rule === "new_account"
            ? "the account is younger than 30 days"
            : details.rule === "no_posts"
              ? "the account published nothing in the 30 days before the start"
              : `the account's history is missing (${details.missingHistory ?? "unknown"})`
        }`;
  return (
    <p>
      {platform === "X" ? "The post has" : "The video's metric is"}{" "}
      {formatCount(details.metric ?? "0")}
      {platform === "X" ? " organic impressions" : ""}. Review is due from{" "}
      {formatCount(details.reviewFrom ?? "0")}, which is {basis}.
    </p>
  );
}

/**
 * A model run's answer in one line. Its verdict, the excerpt or frame it gives as evidence, and
 * its reason, as in `Met "Trying the app offline" at 01:12. It says so.`
 */
function runLine(answer: {
  verdict?: string;
  quote?: string | null;
  timestamp?: string | null;
  frame?: string | null;
  reason?: string;
}): string {
  const verdict = (answer.verdict ?? "").replaceAll("_", " ");
  let line = verdict.charAt(0).toUpperCase() + verdict.slice(1);
  if (answer.quote) {
    line += ` "${answer.quote}"${answer.timestamp ? ` at ${answer.timestamp}` : ""}`;
  }
  if (answer.frame) {
    line += `${answer.quote ? "," : ""} at frame ${answer.frame}`;
  }
  return `${line}. ${answer.reason ?? ""}`.trim();
}

/** A media match review: the durations, and the frames to compare with the public video. */
function MediaEvidence({ review, frames }: { review: Review; frames: ReviewFrame[] }) {
  const { uploadDurationMs, capturedDurationSec } = review.details;
  return (
    <div className="space-y-2">
      {uploadDurationMs !== undefined && capturedDurationSec !== undefined && (
        <p>
          The upload lasts {(uploadDurationMs / 1000).toFixed(1)} seconds. YouTube reported{" "}
          {capturedDurationSec} seconds at submission.
        </p>
      )}
      <Frames frames={frames.filter((frame) => frame.purpose !== "spread")} />
    </div>
  );
}

/** A format review: the video's speech, and the runs of the format's model gate. */
function FormatEvidence({ review }: { review: Review }) {
  const { speech, coverage, segment } = review.details;
  const gate = coverage ?? segment;
  const frames = review.video?.frames ?? [];
  const declared = review.submission.segment;
  return (
    <div className="space-y-2">
      {speech && (
        <p>
          Speech fills {Math.round((speech.speechMs / speech.durationMs) * 100)}% of the video
          {speech.silent ? ". That is less than half, so it fails as SILENT_CONTENT." : "."}
        </p>
      )}
      {gate && <GateEvidence gate={gate} dedicated={coverage !== undefined} />}
      {segment && declared && (
        <Frames
          frames={frames.filter((frame) => frame.at >= declared.start && frame.at <= declared.end)}
        />
      )}
    </div>
  );
}

/** What each run of a format's model gate found. */
function GateEvidence({ gate, dedicated }: { gate: GateRecord; dedicated: boolean }) {
  return (
    <div>
      <p>
        <span className="font-medium">
          {dedicated ? "Share of the video about the brand" : "The segment's promotion"}
        </span>{" "}
        ({gate.outcome.toLowerCase()})
      </p>
      {gate.problem && <p className="text-zinc-600 dark:text-zinc-400">{gate.problem}</p>}
      <ol className="mt-1 list-decimal space-y-1 pl-5">
        {gate.runs?.map((run, index) => {
          const coverage = gate.coverages?.[index];
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: a run has no identity but its place.
            <li key={index}>
              {!run.answer
                ? "No usable answer."
                : dedicated
                  ? `${coverage ? `${Math.round((coverage.covered / coverage.total) * 100)}% about the brand` : "No share"}. Off topic: ${run.answer.offTopic?.join(", ") || "none"}. ${run.answer.explanation ?? ""}`
                  : runLine(run.answer)}
              {run.problem && (
                <span className="text-zinc-500 dark:text-zinc-400"> ({run.problem})</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Frames of the upload, each with a link to the public video at the same time. */
function Frames({ frames }: { frames: ReviewFrame[] }) {
  if (frames.length === 0) {
    return null;
  }
  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {frames.map((frame) => (
        <li key={frame.label}>
          <img
            src={frame.url}
            alt={`The upload at ${frame.label}`}
            loading="lazy"
            className="aspect-video w-full rounded-lg object-cover ring-1 ring-zinc-200 dark:ring-white/10"
          />
          <p className="mt-1 text-xs">
            {frame.label} ({frame.purpose}).{" "}
            <a href={frame.watchUrl} target="_blank" rel="noreferrer" className="underline">
              Public video at {frame.label}
            </a>
          </p>
        </li>
      ))}
    </ul>
  );
}

/** The brand kit's images, which the model compared the content with. */
function BrandKit({ assets }: { assets: { label: string; url: string }[] }) {
  return (
    <div>
      <p className="font-medium">The brand kit, which the model compared the content with</p>
      <ul className="mt-2 flex flex-wrap gap-3">
        {assets.map((asset) => (
          <li key={asset.url} className="w-28">
            {/*
              A light tile on both themes: brand kit images, such as a dark logo on a transparent
              ground, are made to be seen on white.
            */}
            <img
              src={asset.url}
              alt={asset.label}
              className="aspect-square w-full rounded-lg bg-white object-contain ring-1 ring-zinc-200 dark:bg-zinc-100 dark:ring-white/10"
            />
            <p className="mt-1 truncate text-xs">{asset.label}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Morcast's transcript of the upload, folded, with the model that made it. */
function Transcript({
  lines,
  by,
}: {
  lines: { start: number; text: string }[];
  by: string | null;
}) {
  return (
    <Disclosure summary={`Transcript (${lines.length} lines${by ? `, by ${by}` : ""})`}>
      <ol className="max-h-80 space-y-1 overflow-y-auto">
        {lines.map((line) => (
          <li key={`${line.start}-${line.text}`}>
            <span className="text-zinc-500 tabular-nums dark:text-zinc-400">
              [{formatSeconds(line.start)}]
            </span>{" "}
            {line.text}
          </li>
        ))}
      </ol>
    </Disclosure>
  );
}

/** Seconds as "mm:ss", the way transcripts and frames name times. */
function formatSeconds(seconds: number): string {
  const whole = Math.floor(seconds);
  return `${String(Math.floor(whole / 60)).padStart(2, "0")}:${String(whole % 60).padStart(2, "0")}`;
}

/** What the manipulation audit's runs found aimed at the checker. */
function AuditEvidence({ details }: { details: Review["details"] }) {
  const runs = details.runs ?? [];
  if (runs.length === 0) {
    // No run: the card says why, such as a capture that cannot be read.
    return null;
  }
  const found = runs.flatMap((run) => (run.answer?.attempt ? [run.answer] : []));
  return (
    <div>
      <p>
        {found.length} of {runs.length} runs found an attempt:
      </p>
      <ul className="mt-1 list-disc space-y-1 pl-5">
        {found.map((answer, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: runs at temperature 0 often explain alike.
          <li key={index}>
            Quoted {answer.quotes.map((quote) => `"${quote}"`).join(", ")}. {answer.explanation}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A requirement's outcome, with what each run said. */
function RequirementEvidence({
  entry,
}: {
  entry: NonNullable<Review["details"]["requirements"]>[number];
}) {
  return (
    <div>
      <p>
        <span className="font-medium">{entry.requirement}</span> ({entry.outcome.toLowerCase()})
      </p>
      {entry.problem && <p className="text-zinc-600 dark:text-zinc-400">{entry.problem}</p>}
      <ol className="mt-1 list-decimal space-y-1 pl-5">
        {entry.runs?.map((run, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: a run has no identity but its place.
          <li key={index}>
            {run.answer ? runLine(run.answer) : "No usable answer."}
            {run.problem && (
              <span className="text-zinc-500 dark:text-zinc-400"> ({run.problem})</span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

/** A prohibition's outcome, with what each run found. */
function ProhibitionEvidence({
  entry,
}: {
  entry: NonNullable<Review["details"]["prohibitions"]>[number];
}) {
  return (
    <div>
      <p>
        <span className="font-medium">Prohibited: {entry.prohibition}</span> (
        {entry.outcome.toLowerCase()})
      </p>
      <ol className="mt-1 list-decimal space-y-1 pl-5">
        {entry.runs?.map((run, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: a run has no identity but its place.
          <li key={index}>
            {run.answer ? runLine(run.answer) : "No usable answer."}
            {run.problem && (
              <span className="text-zinc-500 dark:text-zinc-400"> ({run.problem})</span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
